import './style.css';

import linkifyHtml from 'linkify-html';
import { Editor } from 'primereact/editor';
import React, { useEffect, useRef, useState } from 'react';
import sanitizeHtml from 'sanitize-html';

type ConsultationRichInputProps = {
  value: string;
  onChange: (html: string) => void; // still needed to update form state
  onBlurSave?: (html: string) => Promise<void>;
  registerRef?: (ref: any) => void;
  editable: boolean;
  error?: boolean;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
};

const ConsultationRichInput = ({
  value,
  onChange,
  onBlurSave,
  registerRef,
  editable,
  error,
  placeholder,
  className,
  style,
}: ConsultationRichInputProps): JSX.Element => {
  const editorRef = useRef<any>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [showFormatting, setShowFormatting] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    setTempValue(value);
  }, [value]);

  useEffect(() => {
    const clickHandler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowFormatting(false);
      }
    };
    document.addEventListener('mousedown', clickHandler);
    return () => document.removeEventListener('mousedown', clickHandler);
  }, []);

  const renderToolbar = () => (
    <div
      style={{
        width: '100%',
        background: '#E5E5E5',
        borderRadius: '6px 6px 0 0',
        padding: '6px 8px',
        alignItems: 'center',
        display: 'flex',
        boxSizing: 'border-box',
        opacity: showFormatting ? 1 : 0,
        pointerEvents: showFormatting ? 'auto' : 'none',
      }}
    >
      <span className="ql-formats" style={{ display: 'flex', gap: '4px' }}>
        <button className="ql-bold" type="button" />
        <button className="ql-italic" type="button" />
        <button className="ql-underline" type="button" />
        <button className="ql-list" value="ordered" type="button" />
        <button className="ql-list" value="bullet" type="button" />
      </span>
    </div>
  );

  const focusEditorFromBox = (e: React.MouseEvent<HTMLDivElement>): void => {
    const target = e.target as HTMLElement;
    const quill = editorRef.current?.getQuill?.();
    if (!quill) return;

    // Clicks on the editing surface or the formatting toolbar keep their
    // native behavior.
    const toolbarEl = editorRef.current?.getToolbar?.() as HTMLElement | null;
    if ((quill.root as HTMLElement).contains(target) || toolbarEl?.contains(target)) {
      return;
    }

    // Clicks anywhere else inside the field box — the border, padding, or
    // blank space below the text — should still drop the caret into the
    // editor instead of doing nothing.
    e.preventDefault();
    quill.focus();
    quill.setSelection(quill.getLength(), 0);
  };

  const sanitizeDisplayHtml = (html: string): string => {
    const linked = linkifyHtml(html || '', {
      defaultProtocol: 'https',
      attributes: { target: '_blank', rel: 'noopener noreferrer' },
    });

    return sanitizeHtml(linked, {
      allowedTags: ['p', 'strong', 'em', 'u', 'ol', 'ul', 'li', 'br', 'span', 'a'],
      allowedAttributes: {
        a: ['href', 'target', 'rel'],
        span: ['class'],
        li: ['data-list'],
      },
    });
  };

  if (!editable) {
    return (
      <div
        className={className}
        style={{
          color: error ? '#BA271A' : '',
          width: '100%',
          padding: editable ? 'unset' : '0.5rem',
          ...style,
        }}
        dangerouslySetInnerHTML={{ __html: sanitizeDisplayHtml(value) }}
      />
    );
  }

  return (
    <div
      ref={wrapperRef}
      style={{ width: '100%' }}
      className={`consultation-rich-input ${showFormatting ? 'has-toolbar' : 'no-toolbar'}  ${
        showFormatting ? 'is-focused' : ''
      }`}
      onMouseDown={focusEditorFromBox}
    >
      <Editor
        ref={(el) => {
          editorRef.current = el;
          if (registerRef) registerRef(el);
        }}
        value={tempValue}
        className={className}
        placeholder={placeholder}
        headerTemplate={renderToolbar()}
        style={
          {
            color: error ? '#BA271A' : '',
            width: '100%',
            ...style,
          } as React.CSSProperties
        }
        onTextChange={(e) => {
          const normalizedValue = e.htmlValue || '';
          setTempValue(normalizedValue as string);
          onChange(normalizedValue);
        }}
        onFocus={() => setShowFormatting(true)}
        onBlur={async () => {
          const normalizedValue = tempValue || '';
          setTempValue(normalizedValue);
          onChange(normalizedValue);
          if (onBlurSave) await onBlurSave(normalizedValue);
        }}
      />
    </div>
  );
};

export default ConsultationRichInput;
